import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

import type { EnterpriseReportRow } from "../api/types";
import { formatDate, formatDateTime, stepTone } from "../lib/format";
import { Pill } from "./ui";

const columnHelper = createColumnHelper<EnterpriseReportRow>();
const ROW_HEIGHT = 52;

/**
 * Virtualized enterprise table. TanStack Table owns the column model; TanStack
 * Virtual renders only the visible rows so a large page stays responsive.
 */
export function EnterpriseTable({
  rows,
  onSelect,
}: {
  rows: EnterpriseReportRow[];
  onSelect: (id: string) => void;
}) {
  const { t, i18n } = useTranslation();

  const columns = useMemo(
    () => [
      // `display_name`, never `name`: a training participant's `name` is the
      // fabricated `formation-<hex>` placeholder, and this is the list a
      // partner scans to find a company. The mobile card below already reads
      // it; the two are the same column at two widths.
      columnHelper.accessor("display_name", {
        header: () => t("reports.columns.name"),
        cell: (info) => (
          <span className="font-medium text-foreground">{info.getValue()}</span>
        ),
      }),
      columnHelper.accessor("city", {
        header: () => t("reports.columns.city"),
      }),
      columnHelper.accessor("region", {
        header: () => t("reports.columns.region"),
        cell: (info) => info.getValue() || "—",
      }),
      columnHelper.accessor((row) => row.partner_group_names.join(", "), {
        id: "partnerGroups",
        header: () => t("reports.columns.partnerGroups"),
        cell: (info) => (
          <span className="line-clamp-1 text-muted-foreground">
            {info.getValue() || "—"}
          </span>
        ),
      }),
      columnHelper.accessor((row) => row.sector_names.join(", "), {
        id: "sectors",
        header: () => t("reports.columns.sectors"),
        cell: (info) => (
          <span className="line-clamp-1 text-muted-foreground">
            {info.getValue() || "—"}
          </span>
        ),
      }),
      columnHelper.accessor("enterprise_type_display", {
        header: () => t("reports.columns.type"),
        cell: (info) => (
          <span className="text-muted-foreground">{info.getValue()}</span>
        ),
      }),
      columnHelper.accessor("current_step", {
        header: () => t("reports.columns.currentStep"),
        cell: (info) => {
          const step = info.getValue();
          return (
            <Pill tone={stepTone(step)}>{t(`reports.steps.${step}`)}</Pill>
          );
        },
      }),
      columnHelper.accessor("last_login", {
        header: () => t("reports.columns.lastLogin"),
        cell: (info) => formatDateTime(info.getValue(), i18n.language) || "—",
      }),
      columnHelper.accessor("account_created", {
        header: () => t("reports.columns.accountCreated"),
        cell: (info) => formatDate(info.getValue(), i18n.language),
      }),
    ],
    [t, i18n.language],
  );

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const scrollRef = useRef<HTMLDivElement>(null);
  const tableRows = table.getRowModel().rows;
  const virtualizer = useVirtualizer({
    count: tableRows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 10,
  });

  return (
    <>
      {/* Mobile: a tap-through card per enterprise. The desktop table crams 7
          columns that would truncate to nothing below md. */}
      <ul className="space-y-2 md:hidden">
        {rows.map((row) => (
          <li key={row.id}>
            <button
              type="button"
              onClick={() => onSelect(row.id)}
              className="w-full rounded-lg border bg-card p-4 text-left transition-colors hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <div className="flex items-start justify-between gap-3">
                <span className="font-semibold text-foreground">{row.display_name}</span>
                <Pill tone={stepTone(row.current_step)}>
                  {t(`reports.steps.${row.current_step}`)}
                </Pill>
              </div>
              <dl className="mt-2 grid grid-cols-[5.5rem_1fr] gap-x-3 gap-y-1 text-sm">
                <dt className="text-muted-foreground">{t("reports.columns.city")}</dt>
                <dd className="text-foreground">
                  {[row.city, row.region].filter(Boolean).join(", ") || "—"}
                </dd>
                <dt className="text-muted-foreground">{t("reports.columns.type")}</dt>
                <dd className="text-foreground">{row.enterprise_type_display}</dd>
                <dt className="text-muted-foreground">{t("reports.columns.sectors")}</dt>
                <dd className="text-foreground">{row.sector_names.join(", ") || "—"}</dd>
                <dt className="text-muted-foreground">{t("reports.columns.lastLogin")}</dt>
                <dd className="text-foreground">
                  {formatDateTime(row.last_login, i18n.language) || "—"}
                </dd>
                <dt className="text-muted-foreground">{t("reports.columns.accountCreated")}</dt>
                <dd className="text-foreground">
                  {formatDate(row.account_created, i18n.language)}
                </dd>
              </dl>
            </button>
          </li>
        ))}
      </ul>

      <div
        ref={scrollRef}
        className="hidden max-h-[60vh] overflow-auto rounded-lg border md:block"
        role="region"
        aria-label={t("reports.tabs.enterprises")}
      >
        <table className="w-full border-collapse text-sm">
        <thead className="sticky top-0 z-10 bg-muted">
          {/* The virtualised body rows are flex rows of equal cells, outside
              the table's own column sizing, so the header row splits the
              width the same way or its labels drift off their columns. */}
          {table.getHeaderGroups().map((group) => (
            <tr key={group.id} className="flex w-full">
              {group.headers.map((header) => (
                <th
                  key={header.id}
                  className="flex-1 truncate px-3 py-2 text-left font-semibold text-foreground"
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody
          style={{ height: `${virtualizer.getTotalSize()}px`, position: "relative" }}
        >
          {virtualizer.getVirtualItems().map((virtualRow) => {
            const row = tableRows[virtualRow.index];
            if (!row) return null;
            return (
              <tr
                key={row.id}
                data-index={virtualRow.index}
                onClick={() => onSelect(row.original.id)}
                className="absolute flex w-full cursor-pointer items-center border-b hover:bg-accent/50"
                style={{
                  height: `${ROW_HEIGHT}px`,
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="flex-1 truncate px-3">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
    </>
  );
}
